/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Light forecasting math for the
 * forecast mockups: moving average, a Holt-Winters-lite fit, TSB for lumpy
 * demand, Syntetos-Boylan classification, outlier rules, accuracy scoring and
 * planning levels. Pure functions, no state, no I/O.
 *
 * These are small honest implementations, not the production engine. They exist
 * so a growth factor, a pinned month or a disabled outlier rule recomputes
 * something real on screen.
 */

export type DemandClass = 'smooth' | 'erratic' | 'intermittent' | 'lumpy';

export function sum(values: readonly number[]): number {
  let total = 0;
  for (const value of values) total += value;
  return total;
}

export function mean(values: readonly number[]): number {
  return values.length === 0 ? 0 : sum(values) / values.length;
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

/** Population standard deviation. */
export function stdev(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const avg = mean(values);
  return Math.sqrt(mean(values.map((value) => (value - avg) ** 2)));
}

/** Linear-interpolated percentile, `p` in 0..100. */
export function percentile(values: readonly number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = (Math.min(100, Math.max(0, p)) / 100) * (sorted.length - 1);
  const low = Math.floor(rank);
  const high = Math.ceil(rank);
  const lowValue = sorted[low] ?? 0;
  const highValue = sorted[high] ?? 0;
  return lowValue + (highValue - lowValue) * (rank - low);
}

/** Pearson correlation of two equal-length series; 0 when degenerate. */
export function correlation(
  a: readonly number[],
  b: readonly number[],
): number {
  const n = Math.min(a.length, b.length);
  if (n < 2) return 0;
  const left = a.slice(0, n);
  const right = b.slice(0, n);
  const meanLeft = mean(left);
  const meanRight = mean(right);
  let cross = 0;
  let varLeft = 0;
  let varRight = 0;
  for (let i = 0; i < n; i += 1) {
    const dl = (left[i] ?? 0) - meanLeft;
    const dr = (right[i] ?? 0) - meanRight;
    cross += dl * dr;
    varLeft += dl * dl;
    varRight += dr * dr;
  }
  const denominator = Math.sqrt(varLeft * varRight);
  return denominator === 0 ? 0 : cross / denominator;
}

export interface FitResult {
  /** One-step-ahead fit for every history month (what the model expected). */
  fitted: number[];
  /** Projection for the horizon, never negative. */
  forecast: number[];
}

function floorAtZero(value: number): number {
  return value < 0 || Number.isNaN(value) ? 0 : value;
}

/** Flat projection at the mean of the last `window` months. */
export function movingAverageFit(
  history: readonly number[],
  horizon: number,
  window = 6,
): FitResult {
  const fitted = history.map((value, index) =>
    index === 0
      ? value
      : mean(history.slice(Math.max(0, index - window), index)),
  );
  const level = mean(history.slice(-window));
  return {
    fitted,
    forecast: Array.from({ length: horizon }, () => level),
  };
}

export interface HoltWintersOptions {
  alpha?: number;
  beta?: number;
  gamma?: number;
  season?: number;
}

export interface HoltWintersFit extends FitResult {
  /** Seasonal index by position in the cycle (history index modulo season). */
  seasonal: number[];
  level: number;
  trend: number;
}

/**
 * Multiplicative Holt-Winters with fixed smoothing constants. Needs two full
 * seasons of history; with less it degrades to a moving average so a caller
 * never has to special-case a short series.
 */
export function holtWintersFit(
  history: readonly number[],
  horizon: number,
  options: HoltWintersOptions = {},
): HoltWintersFit {
  const { alpha = 0.3, beta = 0.05, gamma = 0.25, season = 12 } = options;
  if (history.length < season * 2) {
    const fallback = movingAverageFit(history, horizon);
    return {
      ...fallback,
      seasonal: Array.from({ length: season }, () => 1),
      level: mean(history),
      trend: 0,
    };
  }
  const firstMean = mean(history.slice(0, season));
  const secondMean = mean(history.slice(season, season * 2));
  let level = firstMean;
  let trend = (secondMean - firstMean) / season;
  const seasonal = history
    .slice(0, season)
    .map((value) => (firstMean === 0 ? 1 : Math.max(0.05, value / firstMean)));
  const fitted: number[] = [];
  for (let t = 0; t < history.length; t += 1) {
    const slot = t % season;
    const index = seasonal[slot] ?? 1;
    fitted.push(floorAtZero((level + trend) * index));
    const observed = history[t] ?? 0;
    const previousLevel = level;
    level = alpha * (observed / index) + (1 - alpha) * (level + trend);
    trend = beta * (level - previousLevel) + (1 - beta) * trend;
    const levelSafe = level <= 0 ? 1 : level;
    seasonal[slot] = Math.max(
      0.05,
      gamma * (observed / levelSafe) + (1 - gamma) * index,
    );
  }
  const forecast = Array.from({ length: horizon }, (_, h) => {
    const slot = (history.length + h) % season;
    return floorAtZero((level + (h + 1) * trend) * (seasonal[slot] ?? 1));
  });
  return { fitted, forecast, seasonal, level, trend };
}

export interface TsbOptions {
  alphaProbability?: number;
  alphaSize?: number;
}

export interface TsbFit extends FitResult {
  /** Probability that a month has any demand. */
  probability: number;
  /** Expected size of a demand month. */
  size: number;
}

/**
 * Teunter-Syntetos-Babai: smooth the chance of any demand and the size of a
 * demand month separately, forecast their product. Unlike Croston it decays
 * through a run of zero months, which is how an item going quiet shows up.
 */
export function tsbFit(
  history: readonly number[],
  horizon: number,
  options: TsbOptions = {},
): TsbFit {
  const { alphaProbability = 0.12, alphaSize = 0.2 } = options;
  const seed = history.slice(0, 6);
  const seedDemand = seed.filter((value) => value > 0);
  let probability = seed.length === 0 ? 0 : seedDemand.length / seed.length;
  let size = seedDemand.length === 0 ? 0 : mean(seedDemand);
  const fitted: number[] = [];
  for (const observed of history) {
    fitted.push(probability * size);
    const occurred = observed > 0 ? 1 : 0;
    probability += alphaProbability * (occurred - probability);
    if (occurred === 1) size += alphaSize * (observed - size);
  }
  const level = probability * size;
  return {
    fitted,
    forecast: Array.from({ length: horizon }, () => level),
    probability,
    size,
  };
}

/** Syntetos-Boylan thresholds on average demand interval and CV squared. */
export const ADI_THRESHOLD = 1.32;
export const CV2_THRESHOLD = 0.49;

export interface DemandProfile {
  /** Average months between demands. */
  adi: number;
  /** Squared coefficient of variation of the demand months. */
  cv2: number;
  demandClass: DemandClass;
}

export function profileDemand(history: readonly number[]): DemandProfile {
  const demand = history.filter((value) => value > 0);
  if (demand.length === 0) {
    return { adi: history.length, cv2: 0, demandClass: 'intermittent' };
  }
  const adi = history.length / demand.length;
  const avg = mean(demand);
  const cv2 = avg === 0 ? 0 : (stdev(demand) / avg) ** 2;
  const regular = adi < ADI_THRESHOLD;
  const steady = cv2 < CV2_THRESHOLD;
  const demandClass: DemandClass = regular
    ? steady
      ? 'smooth'
      : 'erratic'
    : steady
      ? 'intermittent'
      : 'lumpy';
  return { adi, cv2, demandClass };
}

/** Months with any demand in the trailing `window` months. */
export function hitsInWindow(
  history: readonly number[],
  window: number,
  endOffset = 0,
): number {
  const end = history.length - endOffset;
  return history
    .slice(Math.max(0, end - window), end)
    .filter((value) => value > 0).length;
}

/** Mean size of the months that had demand. */
export function averageDemandSize(history: readonly number[]): number {
  return mean(history.filter((value) => value > 0));
}

/**
 * Year-over-year repeat: correlation between the first and second year of a
 * 24-month history. A construction-season item scores high; noise scores low.
 */
export function seasonalStrength(history: readonly number[]): number {
  if (history.length < 24) return 0;
  return correlation(history.slice(-24, -12), history.slice(-12));
}

export const SEASONAL_STRENGTH_THRESHOLD = 0.45;

export type MethodChoice = 'tsb' | 'moving_avg' | 'holt_winters';

/**
 * The engine's routing rule: smooth demand with a seasonal signal gets
 * Holt-Winters, intermittent and lumpy demand gets TSB, everything else a
 * moving average.
 */
export function chooseMethod(
  demandClass: DemandClass,
  seasonality: number,
): MethodChoice {
  if (demandClass === 'intermittent' || demandClass === 'lumpy') return 'tsb';
  if (demandClass === 'smooth' && seasonality >= SEASONAL_STRENGTH_THRESHOLD) {
    return 'holt_winters';
  }
  return 'moving_avg';
}

export type OutlierMethod = 'std_dev' | 'median_multiple' | 'percentile';
/**
 * `cap` and `exclude` change the history on their own. `flag` only finds the
 * spike and leaves the decision to a buyer.
 */
export type OutlierAction = 'cap' | 'exclude' | 'flag';

export interface OutlierRuleParams {
  method: OutlierMethod;
  threshold: number;
  action: OutlierAction;
  /** Trailing months judged; null judges the whole history. */
  windowMonths: number | null;
}

export interface OutlierAdjustment {
  /** Index into the history the rule was run on. */
  index: number;
  original: number;
  adjusted: number;
}

export interface OutlierResult {
  adjusted: number[];
  /** Months the rule changed on its own (`cap` and `exclude`). */
  adjustments: OutlierAdjustment[];
  /** Spikes a `flag` rule found, with the typical month it would read them as. */
  flagged: OutlierAdjustment[];
}

/** Fewer demand months than this and nothing is judged. */
export const MIN_DEMAND_MONTHS_FOR_OUTLIERS = 4;

function outlierBar(
  others: readonly number[],
  rule: OutlierRuleParams,
): number {
  const avg = mean(others);
  const mid = median(others);
  switch (rule.method) {
    case 'std_dev':
      // Noise floor of a Poisson count keeps flat low-volume series intact.
      return avg + rule.threshold * Math.max(stdev(others), Math.sqrt(avg));
    case 'median_multiple':
      return rule.threshold * mid;
    case 'percentile':
      return Math.max(
        percentile(others, rule.threshold),
        mid + 2 * Math.sqrt(mid),
      );
  }
}

/**
 * Finds upward demand spikes and caps or excludes them before the forecaster
 * fits (or, for a `flag` rule, only reports them). Every bar is computed from the other demand months (leave-one-out) so a
 * spike cannot hide inside its own sample. "Exclude" reads the month as a
 * typical month, not zero demand.
 */
export function applyOutlierRule(
  history: readonly number[],
  rule: OutlierRuleParams,
): OutlierResult {
  const start =
    rule.windowMonths === null
      ? 0
      : Math.max(0, history.length - rule.windowMonths);
  const demandIndexes: number[] = [];
  for (let i = start; i < history.length; i += 1) {
    if ((history[i] ?? 0) > 0) demandIndexes.push(i);
  }
  if (demandIndexes.length < MIN_DEMAND_MONTHS_FOR_OUTLIERS) {
    return { adjusted: [...history], adjustments: [], flagged: [] };
  }
  const flagged = new Map<number, number>();
  for (const index of demandIndexes) {
    const others = demandIndexes
      .filter((other) => other !== index)
      .map((other) => history[other] ?? 0);
    const bar = outlierBar(others, rule);
    if ((history[index] ?? 0) > bar) flagged.set(index, bar);
  }
  const typical = median(
    demandIndexes
      .filter((index) => !flagged.has(index))
      .map((index) => history[index] ?? 0),
  );
  const adjusted = [...history];
  const adjustments: OutlierAdjustment[] = [];
  const proposals: OutlierAdjustment[] = [];
  for (const [index, bar] of flagged) {
    const original = history[index] ?? 0;
    const replacement = Math.round(rule.action === 'cap' ? bar : typical);
    if (rule.action === 'flag') {
      proposals.push({ index, original, adjusted: replacement });
      continue;
    }
    adjusted[index] = replacement;
    adjustments.push({ index, original, adjusted: replacement });
  }
  return { adjusted, adjustments, flagged: proposals };
}

export interface ForecastAdjustment {
  /** Multiplier on the statistical forecast, 1.08 reads as +8%. */
  factor: number;
  /** First forecast month index the factor applies to. */
  fromIndex: number;
  /** Forecast month index the factor stops before; the whole horizon when absent. */
  toIndex?: number;
  /** Buyer-pinned values by forecast month index. */
  pins: ReadonlyMap<number, number>;
}

/**
 * Pinned months win; the factor scales the rest from `fromIndex` up to, but not
 * including, `toIndex` (the end of the horizon when absent).
 */
export function adjustForecast(
  forecast: readonly number[],
  adjustment: ForecastAdjustment,
): number[] {
  return forecast.map((value, index) => {
    const pinned = adjustment.pins.get(index);
    if (pinned !== undefined) return pinned;
    const inWindow =
      index >= adjustment.fromIndex &&
      (adjustment.toIndex === undefined || index < adjustment.toIndex);
    return Math.round(value * (inWindow ? adjustment.factor : 1));
  });
}

/** Standard deviation of the last `lastN` one-step residuals. */
export function residualSigma(
  history: readonly number[],
  fitted: readonly number[],
  lastN = 12,
): number {
  const residuals = history
    .map((value, index) => value - (fitted[index] ?? 0))
    .slice(-lastN);
  return stdev(residuals);
}

export interface ForecastBand {
  lower: number[];
  upper: number[];
}

/** 80% interval, widening with the horizon. */
export function forecastBand(
  forecast: readonly number[],
  sigma: number,
  z = 1.28,
): ForecastBand {
  const lower: number[] = [];
  const upper: number[] = [];
  forecast.forEach((value, index) => {
    const spread = z * sigma * Math.sqrt(1 + 0.08 * index);
    lower.push(Math.max(0, Math.round(value - spread)));
    upper.push(Math.round(value + spread));
  });
  return { lower, upper };
}

/** Mean absolute month-to-month change: the scale scaled error divides by. */
export function naiveScale(history: readonly number[]): number | null {
  if (history.length < 2) return null;
  const changes: number[] = [];
  for (let i = 1; i < history.length; i += 1) {
    changes.push(Math.abs((history[i] ?? 0) - (history[i - 1] ?? 0)));
  }
  const scale = mean(changes);
  return scale === 0 ? null : scale;
}

export interface AccuracyScore {
  forecast: number;
  actual: number;
  absError: number;
  /** The run's historical naive error; null when it is undefined. */
  naiveError: number | null;
  /** absError divided by naiveError. Under 1 beats the naive forecast. */
  scaledError: number | null;
}

export function scoreMonth(
  forecast: number,
  actual: number,
  scale: number | null,
): AccuracyScore {
  const absError = Math.abs(forecast - actual);
  return {
    forecast,
    actual,
    absError,
    naiveError: scale,
    scaledError: scale === null ? null : absError / scale,
  };
}

export interface PlanningLevelsInput {
  /** Monthly demand the levels follow, starting this month. */
  monthlyDemand: readonly number[];
  /** Monthly forecast error, from the fit's residuals. */
  sigma: number;
  leadTimeDays: number;
  targetAvailability: number;
}

export interface PlanningLevels {
  safetyStock: number;
  /** Demand expected while an order is in transit. */
  leadTimeDemand: number;
  /** Min: the reorder point. */
  min: number;
  /** Max: the order-up-to level, one month of cover above Min. */
  max: number;
}

const Z_BY_AVAILABILITY: readonly (readonly [number, number])[] = [
  [0.9, 1.282],
  [0.95, 1.645],
  [0.975, 1.96],
  [0.99, 2.326],
  [0.999, 3.09],
];

/** The z a target cycle service level implies, from a small lookup. */
export function zForAvailability(target: number): number {
  let best = Z_BY_AVAILABILITY[0];
  for (const entry of Z_BY_AVAILABILITY) {
    if (target >= entry[0]) best = entry;
  }
  return best?.[1] ?? 1.645;
}

/** Demand over `months` (fractional), reading the monthly array from the start. */
export function demandOverMonths(
  monthlyDemand: readonly number[],
  months: number,
): number {
  let remaining = months;
  let total = 0;
  for (const monthDemand of monthlyDemand) {
    if (remaining <= 0) break;
    total += monthDemand * Math.min(1, remaining);
    remaining -= 1;
  }
  return total;
}

export function planningLevels(input: PlanningLevelsInput): PlanningLevels {
  const leadMonths = input.leadTimeDays / 30;
  const leadTimeDemand = demandOverMonths(input.monthlyDemand, leadMonths);
  const safetyStock =
    zForAvailability(input.targetAvailability) *
    input.sigma *
    Math.sqrt(Math.max(leadMonths, 0.25));
  const min = Math.round(leadTimeDemand + safetyStock);
  const cycle = input.monthlyDemand[0] ?? 0;
  return {
    safetyStock: Math.round(safetyStock),
    leadTimeDemand: Math.round(leadTimeDemand),
    min,
    max: Math.round(min + cycle),
  };
}
